import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {StoreService} from '../api/services/store.service';
import {MapService} from '../api/services/map.service';
import {NotificationService} from '../services/notification.service';
import {ThumbnailService} from '../api/services/thumbnail.service';
import {CommonModule} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {SelectInputComponent} from '../shared/forms/select-input/select-input.component';
import {SlideToggleInputComponent} from '../shared/forms/slide-select/slide-toggle-input.component';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {CdkAccordion} from '@angular/cdk/accordion';
import {PublishConfirmationDialogComponent} from './publish-confirmation.dialog';
import {VisibilityWarningDialogComponent} from './visibility-warning.dialog';
import {concatMap, of} from 'rxjs';

@Component({
  selector: 'app-publish-to-store-dialog',
  styles: [`
    .warning-box {
      background-color: #fff3e0;
      border: 1px solid #ffb74d;
      border-radius: 8px;
      padding: 12px 16px;
      margin-bottom: 16px;
    }
    .warning-icon {
      color: #ff9800;
    }
  `],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="material-symbols-rounded me-2">campaign</span>
        {{ 'PUBLISH_STORE.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close
            matTooltip="{{ 'PUBLISH_STORE.CLOSE_TOOLTIP' | translate }}"></span>
    </div>

    <div mat-dialog-content class="p-3 px-4">
      @if (isLoading) {
        <div class="d-flex justify-content-center align-items-center py-5">
          <mat-spinner diameter="40"></mat-spinner>
        </div>
      } @else {
        <cdk-accordion [multi]="true" class="accordion">
          <maporium-accordion-item [title]="'PUBLISH_STORE.ACCORDION.ACCESS' | translate">
            <app-select-input
              [label]="'EMBED_DIALOG.ACCESS.LEVEL' | translate"
              [formControl]="accessControl"
              [options]="accessOptions"
              (selectionChange)="onAccessChange($event)">
            </app-select-input>

            <app-slide-toggle-input
              [formControl]="publishToStoreControl"
              [label]="'PUBLISH_STORE.PUBLISH_TO_STORE_LABEL' | translate"
              (click)="onPublishToggleClick()">
            </app-slide-toggle-input>
          </maporium-accordion-item>
        </cdk-accordion>
      }
    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close [disabled]="isProcessing">{{ 'PUBLISH_STORE.ACTIONS.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary" (click)="save()" [disabled]="isLoading || isProcessing">
        @if (isProcessing) {
          <mat-spinner diameter="20" class="me-2"></mat-spinner>
        }
        {{ 'PUBLISH_STORE.ACTIONS.UPDATE' | translate }}
      </button>
    </div>
  `,
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    ReactiveFormsModule,
    SelectInputComponent,
    SlideToggleInputComponent,
    MaporiumAccordionItemComponent,
    CdkAccordion
  ]
})
export class PublishToStoreDialogComponent implements OnInit {
  isLoading = true;
  isProcessing = false;

  accessControl = new FormControl('private');
  publishToStoreControl = new FormControl(false);
  accessOptions: { key: string, value: string, icon: string }[] = [];

  private initialIsPublic = false;
  private initialIsPublished = false;
  private initialAllowEmbedding = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { map: GraphMap },
    private dialogRef: MatDialogRef<PublishToStoreDialogComponent>,
    private dialog: MatDialog,
    private storeService: StoreService,
    private mapService: MapService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private thumbnailService: ThumbnailService
  ) {}

  ngOnInit() {
    this.initializeOptions();
    this.loadMapData();
  }

  private initializeOptions(): void {
    this.translate.get([
      'EMBED_DIALOG.ACCESS.PUBLIC',
      'EMBED_DIALOG.ACCESS.PRIVATE'
    ]).subscribe(translations => {
      this.accessOptions = [
        { key: 'public', value: translations['EMBED_DIALOG.ACCESS.PUBLIC'], icon: 'public' },
        { key: 'private', value: translations['EMBED_DIALOG.ACCESS.PRIVATE'], icon: 'lock' }
      ];
    });
  }

  private showVisibilityWarning(usage: 'embedding' | 'publishing' | 'publishing and embedding') {
    this.dialog.open(VisibilityWarningDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        mapName: this.data.map.name,
        usage
      }
    });
  }

  onAccessChange(option: {key: string, value: string, icon?: string}) {
    if (option.key === 'private') {
      // User selected private - check if we need to show warning
      if (this.initialAllowEmbedding || this.initialIsPublished) {
        let usage: 'embedding' | 'publishing' | 'publishing and embedding';
        if (this.initialIsPublished && this.initialAllowEmbedding) {
          usage = 'publishing and embedding';
        } else if (this.initialIsPublished) {
          usage = 'publishing';
        } else {
          usage = 'embedding';
        }
        this.showVisibilityWarning(usage);
      }
      this.publishToStoreControl.setValue(false, { emitEvent: false });
    }
  }

  onPublishToggleClick() {
    // If toggle is being turned on and visibility is private, switch to public
    if (!this.publishToStoreControl.value && this.accessControl.value === 'private') {
      this.accessControl.setValue('public');
    }
    // If toggle is being turned off and it was initially on, show warning
    if (this.publishToStoreControl.value && this.initialIsPublished) {
      this.showDeactivatingWarning('publishing');
    }
  }

  private showDeactivatingWarning(deactivating: 'embedding' | 'publishing') {
    this.dialog.open(VisibilityWarningDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        mapName: this.data.map.name,
        usage: deactivating,
        deactivating
      }
    });
  }

  private loadMapData() {
    this.isLoading = true;

    // Load both map data and publish status
    this.mapService.findByIdWithRelation(this.data.map.id).subscribe({
      next: (map) => {
        this.initialIsPublic = map.isPublic ?? false;
        this.initialAllowEmbedding = map.allowWebsiteEmbedding ?? false;
        this.accessControl.setValue(map.isPublic ? 'public' : 'private');

        // Check if map is published
        this.storeService.isPublished(this.data.map.id).subscribe({
          next: (result) => {
            this.initialIsPublished = result.isPublished;
            this.publishToStoreControl.setValue(result.isPublished, { emitEvent: false });
            this.isLoading = false;
          },
          error: (err) => {
            this.isLoading = false;
            this.notificationService.error(this.translate.instant('PUBLISH_STORE.ERROR_CHECK_PUBLISH_STATUS'));
          }
        });
      },
      error: (err) => {
        this.isLoading = false;
        this.notificationService.error(this.translate.instant('PUBLISH_STORE.ERROR_LOAD_MAP_DATA'));
      }
    });
  }

  save() {
    this.isProcessing = true;
    const shouldBePublic = this.accessControl.value === 'public';
    const shouldBePublished = this.publishToStoreControl.value ?? false;

    // Update visibility first, then handle publishing
    const visibilityStream = shouldBePublic
      ? this.mapService.openPublicAccessToMap(this.data.map.id)
      : this.mapService.closePublicAccessToMap(this.data.map.id);

    visibilityStream.pipe(
      concatMap(() => {
        // Handle publishing based on toggle state
        if (shouldBePublished && !this.initialIsPublished) {
          // Generate thumbnail first, then publish the map
          return this.thumbnailService.generateImmediately(this.data.map.id).pipe(
            concatMap((thumbnailUrl) => this.storeService.publishMap(this.data.map.id, thumbnailUrl ?? undefined))
          );
        } else if (!shouldBePublished && this.initialIsPublished) {
          // Delete thumbnail and unpublish the map
          return this.thumbnailService.deleteThumbnail(this.data.map.id).pipe(
            concatMap(() => this.storeService.unpublishMap(this.data.map.id)),
            concatMap(() => of({ published: false }))
          );
        }
        // No change in publish status
        return of({ published: shouldBePublished });
      })
    ).subscribe({
      next: (result) => {
        this.isProcessing = false;

        // Update local map data
        this.data.map.isPublic = shouldBePublic;
        this.mapService.setCurrentSelectedMapToStore(this.data.map);

        // Close this dialog
        this.dialogRef.close({ published: shouldBePublished });

        // Show confirmation dialog only if publish status changed
        if (shouldBePublished !== this.initialIsPublished) {
          this.dialog.open(PublishConfirmationDialogComponent, {
            width: '540px',
            maxWidth: '90vw',
            data: {
              mapName: this.data.map.name,
              published: shouldBePublished
            }
          });
        }
      },
      error: (err) => {
        this.isProcessing = false;
        this.notificationService.error(err.error?.message || this.translate.instant('PUBLISH_STORE.ERROR_SAVE_CHANGES'));
      }
    });
  }
}
